
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Session, User } from '@supabase/supabase-js';
import { toast } from '@/components/ui/sonner';

// Типы пользователей
export type UserRole = 'admin' | 'courier';

export interface UserProfile {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  role: UserRole;
  avatar?: string;
  created_at?: string;
  updated_at?: string;
}

interface AuthContextType {
  user: UserProfile | null;
  session: Session | null;
  isLoading: boolean;
  isAdmin: boolean;
  isCourier: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, role: UserRole, name: string) => Promise<void>;
  logout: () => Promise<void>;
}

// Mock данные для демонстрации
const MOCK_ADMIN: UserProfile = {
  id: '1',
  name: 'Admin User',
  email: 'admin@example.com',
  role: 'admin',
  avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=admin'
};

const MOCK_COURIER: UserProfile = {
  id: '2',
  name: 'Courier User',
  email: 'courier@example.com',
  role: 'courier',
  avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=courier'
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Функция для получения профиля пользователя из Supabase
const fetchUserProfile = async (userId: string): Promise<UserProfile | null> => {
  try {
    // Используем строго типизированный запрос с явным приведением типов
    const { data, error } = await supabase
      .from('profiles')
      .select('id, name, email, phone, role, avatar, created_at, updated_at')
      .eq('id', userId)
      .maybeSingle(); // Изменяем .single() на .maybeSingle() чтобы избежать ошибки

    if (error) {
      console.error('Ошибка при получении профиля:', error);
      return null;
    }

    if (!data) {
      return null;
    }

    // Безопасное приведение типов с проверками
    return {
      id: data.id as string,
      name: data.name as string || 'Пользователь',
      email: data.email as string || '',
      phone: data.phone as string || '',
      role: (data.role as UserRole) || 'courier',
      avatar: data.avatar as string || `https://api.dicebear.com/7.x/avataaars/svg?seed=${data.id}`,
      created_at: data.created_at as string,
      updated_at: data.updated_at as string
    };
  } catch (error) {
    console.error('Ошибка при получении профиля:', error);
    return null;
  }
};

// Функция для очистки состояния аутентификации
export const cleanupAuthState = () => {
  // Удаляем стандартные токены авторизации
  localStorage.removeItem('supabase.auth.token');
  
  // Удаляем все ключи Supabase из localStorage
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
      localStorage.removeItem(key);
    }
  });
  
  // Удаляем из sessionStorage если используется
  Object.keys(sessionStorage || {}).forEach((key) => {
    if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
      sessionStorage.removeItem(key);
    }
  });
};

// Функция для создания или обновления профиля пользователя
const createOrUpdateProfile = async (user: User, role: UserRole = 'admin'): Promise<UserProfile | null> => {
  try {
    const email = user.email || '';
    const name = email ? email.split('@')[0] : (user.phone ? 'Пользователь' : 'Пользователь');
    const avatar = `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.id}`;
    
    // Проверяем, является ли пользователь pshenichnikov@gmail.com для назначения роли администратора
    const assignedRole = email === 'pshenichnikov@gmail.com' ? 'admin' : role;
    
    // Попытка обновить существующий профиль
    const { data: existingProfile, error: selectError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();
      
    if (selectError) {
      console.error('Ошибка при проверке профиля:', selectError);
    }
    
    if (existingProfile) {
      // Обновляем существующий профиль
      const { data, error } = await supabase
        .from('profiles')
        .update({ 
          name: existingProfile.name || name, 
          email: email || existingProfile.email, 
          role: assignedRole,
          avatar: existingProfile.avatar || avatar,
          phone: user.phone || existingProfile.phone || null
        })
        .eq('id', user.id)
        .select()
        .single();
        
      if (error) {
        console.error('Ошибка при обновлении профиля:', error);
        return null;
      }
      
      return data as UserProfile;
    } else {
      // Создаем новый профиль
      const { data, error } = await supabase
        .from('profiles')
        .insert([
          { id: user.id, name, email, role: assignedRole, avatar, phone: user.phone || null }
        ])
        .select()
        .single();
        
      if (error) {
        console.error('Ошибка при создании профиля:', error);
        
        // Особая обработка для pshenichnikov@gmail.com
        if (email === 'pshenichnikov@gmail.com') {
          return {
            id: user.id,
            name: 'Администратор',
            email: 'pshenichnikov@gmail.com',
            role: 'admin',
            avatar: avatar
          };
        }
        
        return null;
      }
      
      return data as UserProfile;
    }
  } catch (error) {
    console.error('Ошибка при создании/обновлении профиля:', error);
    return null;
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Устанавливаем слушатель изменения состояния авторизации
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        console.log('Auth event:', event);
        setSession(currentSession);
        
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          if (currentSession?.user) {
            // Используем setTimeout для предотвращения блокировок
            setTimeout(async () => {
              const userPhone = currentSession.user.phone;
              const userEmail = currentSession.user.email;
              
              console.log('AuthContext: Пользователь авторизован:', userPhone || userEmail);
              
              if (userPhone || userEmail) {
                // Для телефонной авторизации
                if (userPhone) {
                  // Запрос к курьерам, чтобы получить courier.id по номеру телефона
                  try {
                    const { data: courierData, error } = await supabase
                      .from('couriers')
                      .select('*')
                      .eq('phone', userPhone)
                      .limit(1);
                      
                    if (error) {
                      console.error('AuthContext: Ошибка при получении данных курьера:', error);
                    } else if (courierData && courierData.length > 0) {
                      console.log('AuthContext: НАЙДЕН ID КУРЬЕРА В ТАБЛИЦЕ COURIERS:', courierData[0].id);
                      
                      // Проверяем блокировку в таблице profiles
                      const { data: profileData, error: profileError } = await supabase
                        .from('profiles')
                        .select('is_blocked')
                        .eq('phone', userPhone)
                        .single();
                        
                      if (!profileError && profileData?.is_blocked) {
                        console.log('AuthContext: Пользователь заблокирован');
                        await supabase.auth.signOut();
                        return;
                      }
                    } else {
                      console.log('AuthContext: Курьер с таким телефоном не найден в таблице couriers');
                    }
                  } catch (err) {
                    console.error('AuthContext: Ошибка запроса к таблице курьеров:', err);
                  }
                }
                
                 // Получение данных пользователя и установка в контекст
                 const userProfile = await fetchUserProfile(currentSession.user.id);
                 if (userProfile) {
                   // Проверяем блокировку пользователя
                   const { data: blockCheckData } = await supabase
                     .from('profiles')
                     .select('is_blocked')
                     .eq('id', currentSession.user.id)
                     .single();
                     
                   if (blockCheckData?.is_blocked) {
                     console.log('AuthContext: Пользователь заблокирован');
                     await supabase.auth.signOut();
                     return;
                   }
                   
                   setUser(userProfile);
                } else {
                  // Если профиль не найден, создаем новый
                  const email = currentSession.user.email || '';
                  const phone = currentSession.user.phone || '';
                  // Автоматически назначаем администратора для pshenichnikov@gmail.com
                  const role = email === 'pshenichnikov@gmail.com' ? 'admin' : 'courier';
                  const newProfile = await createOrUpdateProfile(currentSession.user, role);
                  if (newProfile) {
                    setUser(newProfile);
                  } else {
                    // Если не удалось создать профиль, используем данные из сессии
                    const userName = email ? email.split('@')[0] : phone ? 'Пользователь' : 'Пользователь';
                    setUser({
                      id: currentSession.user.id,
                      name: userName,
                      email: email || '',
                      phone: phone || '',
                      role: email === 'pshenichnikov@gmail.com' ? 'admin' : 'courier',
                      avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${currentSession.user.id}`
                    });
                  }
                }
              }
              
              setIsLoading(false);
            }, 0);
          }
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setIsLoading(false);
        }
      }
    );

    // Проверяем текущую сессию при загрузке
    supabase.auth.getSession().then(async ({ data: { session: currentSession } }) => {
      setSession(currentSession);
      
      if (currentSession?.user) {
        const userPhone = currentSession.user.phone;
        const userEmail = currentSession.user.email;
        
        console.log('AuthContext (init): Пользователь авторизован:', userPhone || userEmail);
        
        if (userPhone || userEmail) {
          // Для телефонной авторизации
          if (userPhone) {
            // Запрос к курьерам, чтобы получить courier.id по номеру телефона
            try {
              const { data: courierData, error } = await supabase
                .from('couriers')
                .select('*')
                .eq('phone', userPhone)
                .limit(1);
                
              if (error) {
                console.error('AuthContext (init): Ошибка при получении данных курьера:', error);
              } else if (courierData && courierData.length > 0) {
                console.log('AuthContext (init): НАЙДЕН ID КУРЬЕРА В ТАБЛИЦЕ COURIERS:', courierData[0].id);
              } else {
                console.log('AuthContext (init): Курьер с таким телефоном не найден в таблице couriers');
              }
            } catch (err) {
              console.error('AuthContext (init): Ошибка запроса к таблице курьеров:', err);
            }
          }
          
          // Получение данных пользователя и установка в контекст
          const userProfile = await fetchUserProfile(currentSession.user.id);
          if (userProfile) {
            setUser(userProfile);
          } else {
            // Если профиль не найден, создаем новый
            const email = currentSession.user.email || '';
            const phone = currentSession.user.phone || '';
            // Автоматически назначаем администратора для pshenichnikov@gmail.com
            const role = email === 'pshenichnikov@gmail.com' ? 'admin' : 'courier';
            const newProfile = await createOrUpdateProfile(currentSession.user, role);
            if (newProfile) {
              setUser(newProfile);
            } else {
              // Если не удалось создать профиль, используем данные из сессии
              const userName = email ? email.split('@')[0] : phone ? 'Пользователь' : 'Пользователь';
              setUser({
                id: currentSession.user.id,
                name: userName,
                email: email || '',
                phone: phone || '',
                role: email === 'pshenichnikov@gmail.com' ? 'admin' : 'courier',
                avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${currentSession.user.id}`
              });
            }
          }
        }
        
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    });

    // Очистка подписки при размонтировании
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    
    try {
      // Очистка текущего состояния аутентификации
      cleanupAuthState();
      
      // Попытка глобального выхода
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (err) {
        // Продолжаем, даже если это не удалось
        console.log('Ошибка при глобальном выходе:', err);
      }
      
      // В режиме реальной аутентификации через Supabase
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        throw error;
      }

      if (!data.session || !data.user) {
        throw new Error('Не удалось войти в систему');
      }

      // В режиме демонстрации без профилей в БД
      if (email === 'admin@example.com') {
        setUser(MOCK_ADMIN);
      } else if (email === 'courier@example.com') {
        setUser(MOCK_COURIER);
      } else if (email === 'pshenichnikov@gmail.com') {
        // Специальная обработка для pshenichnikov@gmail.com
        const userProfile = await fetchUserProfile(data.user.id);
        if (userProfile) {
          // Если профиль найден, но не админ, обновляем его
          if (userProfile.role !== 'admin') {
            const updatedProfile = await createOrUpdateProfile(data.user, 'admin');
            if (updatedProfile) {
              setUser(updatedProfile);
            } else {
              setUser({...userProfile, role: 'admin'});
            }
          } else {
            setUser(userProfile);
          }
        } else {
          // Если профиль не найден, создаем новый с ролью admin
          const newProfile = await createOrUpdateProfile(data.user, 'admin');
          if (newProfile) {
            setUser(newProfile);
          } else {
            setUser({
              id: data.user.id,
              name: 'Администратор',
              email: 'pshenichnikov@gmail.com',
              role: 'admin',
              avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${data.user.id}`
            });
          }
        }
      } else {
        // Получаем профиль из базы данных
        const userProfile = await fetchUserProfile(data.user.id);
        
        if (userProfile) {
          setUser(userProfile);
        } else {
          // Если профиль не найден, создаем новый
          const newProfile = await createOrUpdateProfile(data.user, 'courier');
          if (newProfile) {
            setUser(newProfile);
          } else {
            // Если не удалось создать профиль, используем данные из сессии
            setUser({
              id: data.user.id,
              name: data.user.email?.split('@')[0] || 'Пользователь',
              email: data.user.email || '',
              phone: data.user.phone || '',
              role: 'courier',
              avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${data.user.id}`
            });
          }
        }
      }
    } catch (error: any) {
      toast.error(`Ошибка входа: ${error.message || 'Неизвестная ошибка'}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (email: string, password: string, role: UserRole, name: string) => {
    setIsLoading(true);
    
    try {
      // Очистка текущего состояния аутентификации
      cleanupAuthState();
      
      // Регистрация через Supabase
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
            role: email === 'pshenichnikov@gmail.com' ? 'admin' : role
          }
        }
      });

      if (error) {
        throw error;
      }

      if (!data.user) {
        throw new Error('Не удалось создать учетную запись');
      }

      // Создаем профиль для нового пользователя
      await createOrUpdateProfile(
        data.user, 
        email === 'pshenichnikov@gmail.com' ? 'admin' : role
      );
      
      toast.success('Учетная запись успешно создана! Проверьте почту для подтверждения.');
    } catch (error: any) {
      toast.error(`Ошибка регистрации: ${error.message || 'Неизвестная ошибка'}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Очищаем состояние аутентификации
      cleanupAuthState();
      
      // Пытаемся выполнить глобальный выход
      await supabase.auth.signOut({ scope: 'global' });
      
      setUser(null);
      setSession(null);
      
      // Перенаправляем на страницу входа
      window.location.href = '/login';
    } catch (error: any) {
      toast.error(`Ошибка выхода: ${error.message || 'Неизвестная ошибка'}`);
    }
  };

  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        session,
        isLoading, 
        isAdmin: user?.role === 'admin',
        isCourier: user?.role === 'courier',
        login, 
        register,
        logout 
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth должен использоваться внутри AuthProvider');
  }
  return context;
};
